import { useState } from 'react'
import { Bell, ChevronDown, HelpCircle, Menu, Search } from 'lucide-react'
import { GlobalSearchPalette } from '@/components/search/GlobalSearchPalette'
import { useGlobalSearchShortcut } from '@/lib/search/useGlobalSearchShortcut'
import { useShortcutRemapToast } from '@/lib/search/useShortcutRemapToast'
import { useGlobalSearchInvokedTelemetry } from '@/lib/search/useGlobalSearchInvokedTelemetry'
import { useSearchEnabled } from '@/lib/search/useSearchEnabled'
import { useOrgRelationshipsEnabled } from '@/lib/org-relationships-flag'
import { ContextSwitcher } from '@/components/layout/ContextSwitcher'
import { SearchInput } from '@/components/ui/SearchInput'
import { ToolSwitcher } from '@/components/layout/ToolSwitcher'
import { useAppTools } from '@/components/layout/app-tools'

interface NavbarProps {
  onMenuClick?: () => void
}

const isMac = typeof navigator !== 'undefined'
  ? (navigator.platform?.toLowerCase().includes('mac') ?? false)
  : true

/**
 * App top navigation bar.
 *
 * ENT-60 changes (T02 + T04):
 * - Center search input is now a read-only palette trigger (not a free-text field).
 * - Placeholder updated to D7 copy.
 * - Cmd-K / Ctrl-K opens GlobalSearchPalette (via useGlobalSearchShortcut).
 * - useShortcutRemapToast fires once per user to inform about ⌘K → ⌘J remap.
 * - useGlobalSearchInvokedTelemetry fires global_search.invoked once per session.
 * - useSearchEnabled: VITE_ENT60_ENABLED=false disables palette (§11.7 kill-switch).
 */
export function Navbar({ onMenuClick }: NavbarProps) {
  const [isPaletteOpen, setIsPaletteOpen] = useState(false)
  const [openedVia, setOpenedVia] = useState<'shortcut' | 'click'>('click')
  const searchEnabled = useSearchEnabled()
  const orgRelationshipsEnabled = useOrgRelationshipsEnabled()
  // ENT-565: the Entiti workspace ↔ CRM switch. Renders nothing for a caller who can open only one tool.
  const appTools = useAppTools()

  // ⌘K / Ctrl+K → open palette (no-op when kill-switch is off)
  useGlobalSearchShortcut(() => {
    if (!searchEnabled) return;
    setOpenedVia('shortcut');
    setIsPaletteOpen(true);
  })

  // One-time remap toast (DD1 / A60.25)
  useShortcutRemapToast()

  // First-open-per-session adoption telemetry (A60.16)
  useGlobalSearchInvokedTelemetry(isPaletteOpen, openedVia)

  return (
    <>
      <header className="h-14 glass sticky top-0 z-40 flex items-center justify-between px-5 sm:px-8 shrink-0 border-b border-border-muted">
        {/* Left */}
        <div className="flex items-center gap-2 sm:gap-3">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="sidebar-overlay p-1.5 text-text-muted hover:text-text rounded-lg transition-colors"
              aria-label="Toggle navigation"
            >
              <Menu size={18} />
            </button>
          )}
          {orgRelationshipsEnabled ? (
            <ContextSwitcher />
          ) : (
            <button className="flex items-center gap-1.5 text-sm font-semibold text-text hover:text-primary transition-colors" aria-label="Switch workspace">
              <span className="hidden sm:inline">Acme Corp</span>
              <span className="sm:hidden">Acme</span>
              <ChevronDown size={13} className="text-text-subtle" />
            </button>
          )}
          <ToolSwitcher tools={appTools.tools} currentId={appTools.currentId} />
        </div>

        {/* Center search — palette trigger (A60.6 / D7) */}
        <div className="flex-1 max-w-lg px-6 sm:px-10 hidden sm:block">
          <div className="relative">
            <SearchInput
              readOnly
              onClick={searchEnabled ? () => { setOpenedVia('click'); setIsPaletteOpen(true); } : undefined}
              placeholder="Find an entity, person, director, shareholder, or owner…"
              className="bg-surface-high pr-16 cursor-pointer focus:bg-surface-elevated"
              aria-label="Global search — press ⌘K or click to open"
              aria-haspopup="dialog"
              aria-expanded={isPaletteOpen}
            />
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 px-1.5 py-0.5 rounded text-[9px] text-text-subtle font-mono bg-surface select-none">
              {isMac ? '⌘' : 'Ctrl+'}K
            </kbd>
          </div>
        </div>

        {/* Mobile search button */}
        <button
          className="sm:hidden p-1.5 text-text-muted hover:text-text rounded-lg transition-colors"
          aria-label="Search"
          onClick={searchEnabled ? () => { setOpenedVia('click'); setIsPaletteOpen(true); } : undefined}
        >
          <Search size={16} />
        </button>

        {/* Right */}
        <div className="flex items-center gap-2 sm:gap-2.5">
          <button className="relative p-2 text-text-muted hover:text-text rounded-lg transition-colors" aria-label="3 unread notifications">
            <Bell size={16} />
            <span className="absolute top-1 right-0.5 min-w-[14px] h-[14px] bg-danger/80 text-[8px] text-danger-foreground flex items-center justify-center rounded-full font-bold px-0.5" aria-hidden="true">
              3
            </span>
          </button>
          <button className="p-2 text-text-muted hover:text-text rounded-lg transition-colors hidden sm:block" aria-label="Help">
            <HelpCircle size={16} />
          </button>
          <button className="ml-1 h-8 w-8 btn-primary rounded-lg flex items-center justify-center text-[10px] font-bold font-display cursor-pointer" aria-label="User menu">
            JS
          </button>
        </div>
      </header>

      {/* Global Search Palette portal (A60.6) — only mounts when kill-switch is on */}
      {searchEnabled && (
        <GlobalSearchPalette
          isOpen={isPaletteOpen}
          onClose={() => setIsPaletteOpen(false)}
        />
      )}
    </>
  )
}
